import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, Star, Archive, ArchiveRestore, RefreshCw, FileText, Eye, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const pdfUrl = (id) => `${API_BASE}/api/coas/${id}/pdf`;
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

export const AdminCoasPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [coas, setCoas] = useState([]);
    const [products, setProducts] = useState([]);
    const [missing, setMissing] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [productFilter, setProductFilter] = useState("");
    const [includeArchived, setIncludeArchived] = useState(true);
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ product_slug: "", manufacturer: "", batch_number: "", notes: "", set_current: true, is_public: true });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, p, m] = await Promise.all([
                api.get("/admin/coas", { headers, params: { include_archived: includeArchived } }),
                api.get("/admin/products", { headers }),
                api.get("/admin/coas/missing", { headers }),
            ]);
            setCoas(c.data);
            setProducts(p.data);
            setMissing(m.data);
        } catch {
            toast.error("Failed to load COAs");
        }
        setLoading(false);
        // eslint-disable-next-line
    }, [includeArchived]);

    useEffect(() => { load(); }, [load]);

    const upload = async (e) => {
        e.preventDefault();
        const f = fileRef.current?.files?.[0];
        if (!f) { toast.error("Pick a PDF first"); return; }
        if (!form.product_slug) { toast.error("Pick a product"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("product_slug", form.product_slug);
        fd.append("manufacturer", form.manufacturer);
        fd.append("batch_number", form.batch_number);
        fd.append("notes", form.notes);
        fd.append("set_current", String(form.set_current));
        fd.append("is_public", String(form.is_public));
        try {
            await api.post("/admin/coas", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
            toast.success("COA uploaded");
            setForm({ product_slug: "", manufacturer: "", batch_number: "", notes: "", set_current: true, is_public: true });
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        }
        setUploading(false);
    };

    const setCurrent = async (id) => {
        try { await api.post(`/admin/coas/${id}/set-current`, {}, { headers }); toast.success("Set as current"); load(); }
        catch { toast.error("Action failed"); }
    };
    const toggleArchive = async (c) => {
        try { await api.patch(`/admin/coas/${c.id}`, { is_archived: !c.is_archived }, { headers }); load(); }
        catch { toast.error("Action failed"); }
    };
    const togglePublic = async (c) => {
        try { await api.patch(`/admin/coas/${c.id}`, { is_public: !c.is_public }, { headers }); load(); }
        catch { toast.error("Action failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Permanently delete this COA?")) return;
        try { await api.delete(`/admin/coas/${id}`, { headers }); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const visibleCoas = coas.filter((c) => {
        if (productFilter && c.product_slug !== productFilter) return false;
        if (!filter) return true;
        const q = filter.toLowerCase();
        return [c.product_name, c.batch_number, c.manufacturer, c.filename].some((v) => (v || "").toLowerCase().includes(q));
    });

    return (
        <div data-testid="admin-coas-panel">
            {missing.length > 0 && (
                <div
                    className="mb-5 glass-elevated rounded-2xl p-4 border border-amber-500/40 bg-amber-500/5 flex items-start gap-3"
                    data-testid="admin-coas-missing-alert"
                >
                    <AlertTriangle size={18} className="text-amber-300 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-amber-100 font-display font-bold mb-1">
                            {missing.length} product{missing.length === 1 ? "" : "s"} have no active COA
                        </div>
                        <div className="text-xs text-amber-200/80 mb-2">Orders containing these products won&apos;t include a COA attachment in the customer email.</div>
                        <div className="flex flex-wrap gap-1.5">
                            {missing.map((p) => (
                                <button
                                    key={p.slug}
                                    onClick={() => setForm({ ...form, product_slug: p.slug })}
                                    className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
                                    data-testid={`admin-coas-missing-${p.slug}`}
                                >
                                    {p.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Upload form */}
            <form onSubmit={upload} className="glass-elevated rounded-2xl p-5 mb-6 border border-[var(--gold)]/20" data-testid="admin-coa-upload-form">
                <div className="flex items-center gap-2 mb-4">
                    <Upload size={16} className="text-[var(--gold)]" />
                    <h3 className="font-display text-lg font-bold text-white">Upload new COA</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <select
                        required
                        value={form.product_slug}
                        onChange={(e) => setForm({ ...form, product_slug: e.target.value })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-coa-product"
                    >
                        <option value="">Product…</option>
                        {products.map((p) => (<option key={p.slug} value={p.slug}>{p.name}</option>))}
                    </select>
                    <input
                        type="text"
                        placeholder="Manufacturer"
                        value={form.manufacturer}
                        onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-coa-manufacturer"
                    />
                    <input
                        type="text"
                        placeholder="Batch / Lot number"
                        value={form.batch_number}
                        onChange={(e) => setForm({ ...form, batch_number: e.target.value })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-coa-batch"
                    />
                    <input
                        type="text"
                        placeholder="Internal notes (optional)"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-coa-notes"
                    />
                </div>
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" required className="block w-full text-sm text-white mb-3 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-widest file:bg-[var(--gold)] file:text-black hover:file:bg-[#F5C932]" data-testid="admin-coa-file" />
                <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
                        <input type="checkbox" checked={form.set_current} onChange={(e) => setForm({ ...form, set_current: e.target.checked })} className="w-4 h-4 accent-[var(--gold)]" data-testid="admin-coa-set-current" />
                        Set as current
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
                        <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 accent-[var(--gold)]" data-testid="admin-coa-public" />
                        Public (visible to customers)
                    </label>
                </div>
                <button type="submit" disabled={uploading} className="btn-solid" data-testid="admin-coa-upload-submit">
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload COA"}
                </button>
            </form>

            {/* List filters */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search batch / product / manufacturer…"
                        className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="admin-coas-search"
                    />
                </div>
                <select
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="admin-coas-product-filter"
                >
                    <option value="">All products</option>
                    {products.map((p) => (<option key={p.slug} value={p.slug}>{p.name}</option>))}
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
                    <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="w-4 h-4 accent-[var(--gold)]" data-testid="admin-coas-show-archived" />
                    <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Include archived</span>
                </label>
                <button onClick={load} className="btn-neon" data-testid="admin-coas-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading…</div>
            ) : visibleCoas.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-coas-empty">
                    <FileText size={28} className="mx-auto mb-2 text-[var(--text-muted)]" />
                    No COAs to show.
                </div>
            ) : (
                <div className="space-y-2">
                    {visibleCoas.map((c) => (
                        <div key={c.id} className="glass-elevated rounded-xl p-4" data-testid={`admin-coa-row-${c.id}`}>
                            <div className="flex items-start gap-4 flex-wrap">
                                <div className="w-10 h-10 shrink-0 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                                    <FileText size={16} className="text-emerald-300" />
                                </div>
                                <div className="flex-1 min-w-[220px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-bold text-white">{c.product_name}</span>
                                        {c.is_current && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-[var(--gold)]/40 text-[var(--gold)]">Current</span>}
                                        {c.is_archived && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-white/10 text-white/40">Archived</span>}
                                        {!c.is_public && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-200">Hidden</span>}
                                    </div>
                                    <div className="text-xs text-[var(--text-secondary)] mt-1 flex items-center gap-3 flex-wrap">
                                        {c.batch_number && <span className="font-mono">{c.batch_number}</span>}
                                        {c.manufacturer && <span>· {c.manufacturer}</span>}
                                        <span>· {fmt(c.upload_date)}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <a href={pdfUrl(c.id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-white/10 text-white hover:border-[var(--gold)]/40 hover:text-[var(--gold)]" aria-label="View" data-testid={`admin-coa-view-${c.id}`}><Eye size={13} /></a>
                                    {!c.is_current && (
                                        <button onClick={() => setCurrent(c.id)} className="p-2 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10" aria-label="Set current" data-testid={`admin-coa-set-current-${c.id}`} title="Set as current"><Star size={13} /></button>
                                    )}
                                    <button onClick={() => togglePublic(c)} className="p-2 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/30" aria-label="Toggle public" data-testid={`admin-coa-toggle-public-${c.id}`} title={c.is_public ? "Hide from customers" : "Show to customers"}>{c.is_public ? <Eye size={13} /> : <Search size={13} />}</button>
                                    <button onClick={() => toggleArchive(c)} className="p-2 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/30" aria-label="Toggle archive" data-testid={`admin-coa-toggle-archive-${c.id}`} title={c.is_archived ? "Unarchive" : "Archive"}>{c.is_archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}</button>
                                    <button onClick={() => del(c.id)} className="p-2 rounded-full text-[var(--text-muted)] hover:text-red-400" aria-label="Delete" data-testid={`admin-coa-delete-${c.id}`}><Trash2 size={13} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
